/**
 * What a programme entry IS, when it is not a music set (the user, 2026-10-10).
 *
 * Lineups arrive as one line of text per entry, and a workshop's line carries
 * three facts at once: "Yin Yoga - Orsolya Ambrus" is the SESSION "Yin Yoga",
 * the ACTIVITY yoga (in the Body & Mind category) and the PERSON leading it.
 * Imported as-is, the whole line became an artist's name — "Soulful Yoga w
 * Maura Rath" sat in the artist directory next to Shpongle, on the Music track.
 *
 * `parseSession` takes the line apart. It is deliberately conservative,
 * because the failure modes are not symmetric: a yoga class left as a music
 * act is a cosmetic defect an admin can fix, while a band turned into a
 * workshop (Trash Talk, Morgue Breath, Ceremony, CATCH YOUR BREATH) loses its
 * artist profile. So:
 *
 * - on a MUSIC act (`knownSession: false`) only an unambiguous word makes a
 *   session — "yoga", "breathwork", "workshop" — or a weaker one ("talk",
 *   "ceremony", "breath") WITH a person attached ("Factory talk - James Nice");
 * - a person is taken out only when one side of the line is clearly the
 *   activity and the other side clearly a name. Ambiguous lines stay whole:
 *   "Gábor Simonovits - Ozorian Subcultures" could be either way round.
 *
 * Pure: it runs in the importers, the API and the browser alike.
 */

export type SessionCategory = 'bodymind' | 'artcraft';

export interface Activity {
  /** Stored on `Act.activity`. Lowercase, stable — a filter value, not a label. */
  readonly id: string;
  readonly label: string;
  readonly category: SessionCategory;
  /** Its emoji — what a session shows where it has no picture of its own (the user, 2026-11-02: "yoga shows yoga"). */
  readonly icon: string;
}

/** Each activity's emoji, by id. */
export const ACTIVITY_ICONS: Readonly<Record<string, string>> = {
  yoga: '🧘', breathwork: '🌬️', 'sound-journey': '🔔', meditation: '🪷', 'tai-chi': '☯️', pilates: '🤸',
  'martial-arts': '🥋', massage: '💆', movement: '💃', healing: '🌿', fitness: '🏃', talk: '🎤', workshop: '🎨', film: '🎬',
};

interface Rule extends Omit<Activity, 'icon'> {
  /** Matches on its own, even on a music act. */
  readonly strong: RegExp;
  /** Matches only on a known session, or with a person attached. */
  readonly weak?: RegExp;
}

// Order matters: the first rule that matches names the activity, so the
// specific ("yoga nidra" is yoga, "sound meditation" is a sound journey) comes
// before the general.
const RULES: readonly Rule[] = [
  { id: 'yoga', label: 'Yoga', category: 'bodymind', strong: /\byoga\b|\bvinyasa\b|\bhatha\b|\bashtanga\b/i },
  { id: 'breathwork', label: 'Breathwork', category: 'bodymind', strong: /\bbreath ?work\b|\bpranayama\b|\bwim hof\b|\bice bath\b/i, weak: /\bbreath(e|ing)?\b/i },
  { id: 'sound-journey', label: 'Sound journey', category: 'bodymind', strong: /\bsound (bath|journey|healing|meditation|ritual|circle|garden)\b|\bgong (bath|journey)\b/i, weak: /\bgong\b|\bmantra\b/i },
  { id: 'meditation', label: 'Meditation', category: 'bodymind', strong: /\bmeditation\b|\bmindfulness\b|\bnidra\b/i, weak: /\bmeditat/i },
  { id: 'tai-chi', label: 'Tai chi & qigong', category: 'bodymind', strong: /\btai ?chi\b|\bqi ?gong\b/i },
  { id: 'pilates', label: 'Pilates', category: 'bodymind', strong: /\bpilates\b/i },
  { id: 'martial-arts', label: 'Martial arts', category: 'bodymind', strong: /\bjiu ?jitsu\b|\bbjj\b|\baikido\b|\bkickbox(ing)?\b|\bcapoeira\b|\bkung fu\b|\bkenkodo\b/i },
  { id: 'massage', label: 'Massage & bodywork', category: 'bodymind', strong: /\bmassage\b|\breiki\b|\bbodywork\b/i },
  { id: 'movement', label: 'Movement & dance', category: 'bodymind', strong: /\becstatic dance\b|\bconscious movement\b|\bcontact (dance|improv)|\bsomatic\b|\bdance (workshop|class)\b|\bdancing workshop\b|\bline ?danc(e|ing)\b|\bhandstand\b|\bacro\b/i, weak: /\bdanc(e|ing)\b|\bmovement\b|\bmove\b|\bflow\b/i },
  { id: 'healing', label: 'Healing', category: 'bodymind', strong: /\bcacao ceremony\b|\bhealing circle\b|\bchakra\b/i, weak: /\bhealing\b|\bceremony\b|\bcircle\b|\britual\b/i },
  { id: 'fitness', label: 'Fitness', category: 'bodymind', strong: /\bfitness\b|\bbootcamp\b|\brun club\b|\bhiit\b/i },
  { id: 'talk', label: 'Talk', category: 'bodymind', strong: /\btalks in\b|\bpanel\b|\blecture\b|\bin conversation\b|\bround ?table\b|\bbook (signing|reading)\b|\bkeynote\b|\bpodcast\b/i, weak: /\btalks?\b|\bq ?& ?a\b|\bdiscussion\b/i },
  { id: 'workshop', label: 'Workshop', category: 'artcraft', strong: /\bworkshops?\b|\b(hat|zine|patch|jewel(le)?ry|mask|candle|soap|snack)s? making\b|\bbody ?painting\b|\bzine\b|\bfelting\b|\bjuggling\b|\bcollage\b|\bmosaic\b|\bpottery\b|\bceramics\b|\bmacram/i, weak: /\bcrafts?\b|\bpainting\b|\bmaking\b/i },
  { id: 'film', label: 'Film', category: 'artcraft', strong: /\bfilm screening\b|\bscreening\b/i },
];

/** Every activity, for a filter or an admin picker. */
export const ACTIVITIES: readonly Activity[] = RULES.map(({ id, label, category }) => ({ id, label, category, icon: ACTIVITY_ICONS[id] ?? '✨' }));

export function activityById(id: string | null | undefined): Activity | undefined {
  return id ? ACTIVITIES.find((a) => a.id === id) : undefined;
}

/**
 * Which category a session BELONGS in (the user, 2026-11-02: "yoga does not belong to the art category — when pulling
 * data it needs to be checked as well … in the AI modules, the wizard and the rules"): its activity's — the one it was
 * given, else the one its name reads as, its own category asked first (so "Body Painting, Yoga" on Art & Craft stays a
 * workshop). Null when it is no session or names no known activity: then it stays where it was put.
 */
export function sessionCategoryFor(name: string, activityId: string | null | undefined, placedIn: SessionCategory | null): SessionCategory | null {
  const given = activityById(activityId);
  if (given) return given.category;
  if (!placedIn) return null;
  return parseSession(name, { knownSession: true, category: placedIn })?.activity?.category ?? null;
}

/**
 * The activities that are ONE category's practice — a yoga class is Body & Mind wherever it is filed. A workshop, a
 * talk, movement and dance, a healing circle are FORMATS both categories host ("Heartwave Telepathy Workshop" is Body
 * & Mind, a drum and dance circle Art & Craft), so they never move a session.
 */
export const ACTIVITY_FIRM: ReadonlySet<string> = new Set([
  'yoga', 'breathwork', 'sound-journey', 'meditation', 'tai-chi', 'pilates', 'martial-arts', 'massage', 'fitness', 'film',
]);

/** A session put in the other session category than its FIRM activity's — "Yoga" on Art & Craft. */
export function sessionMisplaced(name: string, activityId: string | null | undefined, placedIn: SessionCategory | null): SessionCategory | null {
  if (!placedIn) return null;
  const given = activityById(activityId);
  const activity = given ?? parseSession(name, { knownSession: true, category: placedIn })?.activity ?? null;
  if (!activity || !ACTIVITY_FIRM.has(activity.id)) return null;
  return activity.category !== placedIn ? activity.category : null;
}

export interface ParsedSession {
  /** The session's own title, the person removed: "Yin Yoga". */
  readonly title: string;
  /** null when the line is a session but names no known activity. */
  readonly activity: Activity | null;
  /** The people leading it, in order. Empty when none could be told apart safely. */
  readonly people: readonly string[];
}

function ruleFor(text: string, allowWeak: boolean, prefer?: SessionCategory): Rule | null {
  const pick = (rules: readonly Rule[]) => {
    for (const r of rules) if (r.strong.test(text)) return r;
    if (allowWeak) for (const r of rules) if (r.weak?.test(text)) return r;
    return null;
  };
  // On a known Art & Craft act, "Body Painting, Yoga" is a workshop that
  // mentions yoga, not a yoga class: the act's own category is asked first.
  return (prefer ? pick(RULES.filter((r) => r.category === prefer)) : null) ?? pick(RULES);
}

const hasAnyActivityWord = (text: string) => ruleFor(text, true) !== null;
const hasStrongActivityWord = (text: string) => ruleFor(text, false) !== null;

/** Words that are never a person, though they arrive where a name would. */
const NOT_A_PERSON = /^(kids|children|family|families|teens?|adults|all ages|guests?|special guests?|friends|everyone|beginners|edition|edtion|assembly|fretting|intro|part \d+)$/i;

/** Lowercase words a person's name may carry: "Laura de la Cruz", "Jan van Dijk". */
const NAME_PARTICLES = new Set(['de', 'da', 'di', 'del', 'della', 'la', 'le', 'van', 'von', 'der', 'den', 'du', 'dos', 'das', 'el', 'al', 'bin', 'ibn', 'y', 'dj', 'mc', 'dr', 'd.']);

/**
 * Does this read as one person's (or one act's) name? Capitalised words, at
 * most five, no activity word, no digits. "Orsolya Ambrus", "NéliaHolistica",
 * "Dr Nachi Oliva M.D." yes; "Guidelines for Safer Partying" no.
 */
function looksLikeName(text: string): boolean {
  const t = text.trim();
  if (t.length < 2 || t.length > 40 || /[\d:!?()/]/.test(t) || NOT_A_PERSON.test(t) || hasAnyActivityWord(t)) return false;
  if (/\bedi?tion\b|\bedtion\b/i.test(t)) return false;
  const words = t.split(/\s+/);
  if (words.length > 5) return false;
  // A lone dash joins a double-barrelled name — "Krisztina Szeder - Szabo" —
  // once: "Remember - Forget - Embody" is a title.
  if (words.filter((w) => w === '-').length > 1) return false;
  return words.every((w) => w === '-' || NAME_PARTICLES.has(w.toLowerCase()) || /^[\p{Lu}\p{Lt}]/u.test(w) || /^[\p{Lo}]/u.test(w));
}

/** "Viktor Nemeth D. & Intiche", "Paula Sita&Julia Anjuli" → each person, or null if any part is not a name. */
function splitPeople(text: string): string[] | null {
  const parts = text.split(/\s*(?:&|\+|,|\band\b|\bx\b|\bft\.?|\bfeat\.?)\s*|\s+(?:with|w\/|w\.)\s+/i).map((p) => p.trim()).filter(Boolean);
  if (parts.length === 0) return null;
  return parts.every(looksLikeName) ? parts : null;
}

/** Separators between a session and its leader, most specific first. */
const LEADER = /\s+(?:with|w\/|w\.|w|by|led by|hosted by|met|mit|avec|door)\s+(?=\S)/i;
const DASH = /\s+[-–—]\s*|\s*[-–—]\s+/;
/** The end of a title that already names its leader: "… Podcast w. Krisztina Szeder". */
const LEADER_AT_END = /\s(?:with|w\/|w\.|w|by|led by|hosted by)\s+[\p{Lu}][\p{L}'’.-]*(?:\s+[\p{Lu}][\p{L}'’.-]*){0,3}$/u;
const COLON = /\s*:\s+/;

/**
 * Take a programme line apart, or say it is not a session (null).
 *
 * `knownSession`: the line is already on a Body & Mind / Art & Craft track (as
 * OZORA's are), so the question is only how to split it, never whether it is
 * one — and weak activity words count.
 */
export function parseSession(
  name: string,
  opts: { knownSession?: boolean; category?: SessionCategory } = {},
): ParsedSession | null {
  const raw = name.replace(/\s+/g, ' ').trim();
  if (!raw) return null;
  const known = !!opts.knownSession;

  // "Himalayan Kundalini … (Ginger · Yoga, Breathwork)": a trailing bracket
  // naming a person and/or activities.
  let line = raw;
  let bracketPeople: string[] = [];
  const bracket = /\s*\(([^()]+)\)\s*$/.exec(line);
  if (bracket) {
    const inner = bracket[1]!.split(/\s*[·|]\s*/);
    const named = inner.filter((p) => !hasAnyActivityWord(p));
    const people = named.length ? splitPeople(named.join(' & ')) : [];
    if (people && inner.some(hasAnyActivityWord)) {
      bracketPeople = people;
      line = line.slice(0, bracket.index).trim();
    }
  }

  let title = line;
  let people: string[] = bracketPeople;

  const tryLeader = () => {
    const m = LEADER.exec(line);
    if (!m) return false;
    const before = line.slice(0, m.index).trim();
    const after = line.slice(m.index + m[0].length).trim();
    const who = splitPeople(after);
    // The part before "with" must itself be the activity, even on a known
    // session: "Pact with Flames" and "Dancing with Goddesses" name no person.
    if (!before || !who || !(known ? hasAnyActivityWord(before) : hasStrongActivityWord(before))) return false;
    title = before; people = [...who, ...people];
    return true;
  };
  /**
   * Split at a separator into title and people. Every occurrence is tried,
   * the LAST first: "Butoh- Inner movement - Gyöngy Dániel" splits before the
   * person, not after "Butoh". `byNameAlone` allows the no-activity-word case
   * (dashes on a known session only — a colon is too often part of a title).
   */
  const trySplit = (sep: RegExp, byNameAlone: boolean) => {
    const cuts = [...line.matchAll(new RegExp(sep.source, 'gi'))].reverse();
    for (const m of cuts) {
      const left = line.slice(0, m.index).trim();
      const right = line.slice(m.index! + m[0].length).trim();
      if (!left || !right) continue;
      // On a music act the activity side must be UNAMBIGUOUS: "PCK Dance:
      // Vessel" is a dance company's show, "CLIP DANCE: Alina Pash – Slukhay"
      // a music video. A weak word only marks which side is not the person.
      const is = known ? hasAnyActivityWord : hasStrongActivityWord;
      // "…Podcast w. Krisztina Szeder - Szabo, …": a dash right after a
      // leader's name is inside the name, not between title and leader.
      if (sep === DASH && LEADER_AT_END.test(left)) continue;
      const leftIs = is(left) && !(hasAnyActivityWord(right));
      const rightIs = is(right) && !(hasAnyActivityWord(left));
      // Exactly one side is the activity; the other must read as people.
      if (leftIs && !rightIs) {
        const who = splitPeople(right);
        if (who) { title = left; people = [...who, ...people]; return true; }
      } else if (rightIs && !leftIs) {
        const who = splitPeople(left);
        if (who) { title = right; people = [...who, ...people]; return true; }
      } else if (byNameAlone && !hasAnyActivityWord(left) && !hasAnyActivityWord(right)) {
        // No activity word on either side: split only when exactly ONE side
        // reads as a name. "Peter Sarosi - Guidelines for Safer Partying" yes;
        // "Gábor Simonovits - Ozorian Subcultures" stays whole.
        // The weakest evidence, so no "x" collaboration on the name side:
        // "Dhrupad x Ableton Sound Immersion - Sahitya" is not led by its title.
        const collab = (t: string) => /\sx\s/i.test(t);
        const l = collab(left) ? null : splitPeople(left);
        const r = collab(right) ? null : splitPeople(right);
        if (l && !r) { title = right; people = [...l, ...people]; return true; }
        if (r && !l) { title = left; people = [...r, ...people]; return true; }
      }
    }
    return false;
  };

  // A dash first: "Person - Title with Guest" is split at the dash, not at "with".
  if (!trySplit(DASH, known) && !tryLeader()) trySplit(COLON, false);

  const weakOk = known || people.length > 0;
  const rule = ruleFor(title, weakOk, opts.category) ?? ruleFor(raw, weakOk, opts.category);
  if (!rule && !known) return null;
  return { title, activity: rule ? { id: rule.id, label: rule.label, category: rule.category, icon: ACTIVITY_ICONS[rule.id] ?? '✨' } : null, people };
}
